import React, { useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import { Input, InputGroup } from '@components'
import Breadcrumb from '@components/Breadcrumb'
import Button from '@components/Button'
import Divider from '@components/Divider'
import Flex from '@components/Flex'
import PageTitle from '@components/PageTitle'
import Spinner from '@components/Spinner'
import Status from '@components/Status'
import Template from '@components/Template'
import { legalDocumentApi } from '@services/LegalDocumentApi'
import { createBreadcrumbLink, useCreateBreadcrumb } from '@utils/hooks'

// Abaixo disto a norma quase certamente entrou pela metade. Súmula e tema
// avulsos têm um dispositivo só, e por isso o alerta olha o tipo antes de
// acender.
const SUSPICIOUSLY_FEW_PROVISIONS = 5
const SINGLE_PROVISION_KINDS = ['sumula', 'sumula-vinculante', 'tema']

/*
 * O documento subido pelo admin fica guardado como `admin://arquivo`, que é
 * marcador interno e não endereço. Só a norma capturada da web tem de onde ter
 * vindo, e é só nela que a linha aparece — mostrar um protocolo que não existe
 * seria oferecer um link que não abre.
 */
const isOfficialAddress = (url: string | null): boolean =>
	Boolean(url && /^https?:\/\//u.test(url))

function Field({
	label,
	children,
}: {
	label: string
	children: React.ReactNode
}) {
	return (
		<div>
			<span className='block text-sm text-gray'>{label}</span>
			<span className='block font-medium break-words'>{children}</span>
		</div>
	)
}

function LegalDocumentDetail() {
	const { id } = useParams<{ id: string }>()
	const navigate = useNavigate()

	const documentId = id ?? ''
	const baseUrl = '/acervo'

	const [confirmingRemoval, setConfirmingRemoval] = useState(false)
	const [editing, setEditing] = useState(false)
	const [draftTitle, setDraftTitle] = useState('')
	const [draftScopes, setDraftScopes] = useState('')

	const [draftNicknames, setDraftNicknames] = useState('')

	const allPages = useMemo(
		() => [
			createBreadcrumbLink('Acervo de documentos', baseUrl, 1),
			createBreadcrumbLink('Documento', `${baseUrl}/${id}`, 2),
		],
		[id]
	)

	const pages = useCreateBreadcrumb(allPages)

	const { data: document, isLoading } = legalDocumentApi.useShowQuery(
		{ id: documentId },
		{ skip: !id }
	)

	const [removeDocument, { isLoading: isRemoving }] =
		legalDocumentApi.useRemoveMutation()

	const [retitle, { isLoading: isRetitling, error: retitleError }] =
		legalDocumentApi.useRetitleMutation()

	if (isLoading || !document) {
		return (
			<Template>
				<Breadcrumb pages={pages} />

				<div className='flex justify-center mt-10'>
					<Spinner size='md' />
				</div>
			</Template>
		)
	}

	const formatDate = (value: string) =>
		new Date(value).toLocaleString('pt-BR', {
			dateStyle: 'short',
			timeStyle: 'short',
		})

	const looksTruncated =
		document.provisionCount < SUSPICIOUSLY_FEW_PROVISIONS &&
		!SINGLE_PROVISION_KINDS.includes(document.kind)

	const onRemove = async () => {
		await removeDocument({ id: documentId })

		navigate(baseUrl)
	}

	const openEditor = () => {
		setDraftTitle(document.title)
		setDraftScopes(document.scopes.join(', '))
		setDraftNicknames(document.aliases.join(', '))
		setEditing(true)
	}

	/*
	 * O endereço muda junto com o título, então a tela precisa seguir para o
	 * novo: a rota atual deixa de existir no instante em que a norma se move.
	 */
	const onRetitle = async () => {
		const updated = await retitle({
			id: documentId,
			title: draftTitle,
			scopes: draftScopes
				.split(',')
				.map((scope) => scope.trim())
				.filter((scope) => scope.length > 0),
			nicknames: draftNicknames
				.split(',')
				.map((nickname) => nickname.trim())
				.filter((nickname) => nickname.length > 0),
		}).unwrap()

		setEditing(false)

		if (updated.slug !== documentId) {
			navigate(`${baseUrl}/${updated.slug}`, { replace: true })
		}
	}

	const retitleFailure = (
		retitleError as { data?: { message?: string; detail?: string } }
	)?.data

	return (
		<Template>
			<Breadcrumb pages={pages} />
			<PageTitle title={document.title} />

			<Flex className='gap-4 items-start mt-4 flex-wrap'>
				<Status
					status={document.origin === 'oficial' ? 'success' : 'warn'}
					className='px-3 py-[6px] text-sm'
				>
					{document.origin === 'oficial'
						? 'Documento oficial'
						: 'Recorte do DLS'}
				</Status>

				<div className='flex-1' />

				{confirmingRemoval ? (
					<Flex className='gap-2'>
						<Button onClick={onRemove} disabled={isRemoving}>
							{isRemoving ? 'Retirando...' : 'Confirmar retirada'}
						</Button>
						<Button
							variant='outline'
							onClick={() => setConfirmingRemoval(false)}
						>
							<>Cancelar</>
						</Button>
					</Flex>
				) : (
					<Flex className='gap-2'>
						<Button variant='outline' onClick={openEditor}>
							<>Corrigir dados</>
						</Button>
						<Button
							variant='outline'
							onClick={() => setConfirmingRemoval(true)}
						>
							<>Retirar do acervo</>
						</Button>
					</Flex>
				)}
			</Flex>

			{editing && (
				<div className='mt-6 p-6 bg-white rounded-sm shadow-5'>
					<p className='text-gray max-w-[820px] mb-5 leading-relaxed'>
						O título é o endereço da norma. Corrigi-lo move a norma inteira,
						levando dispositivos e apelidos junto. O endereço novo não pode
						pertencer a outra norma.
					</p>

					<div className='grid grid-cols-2 gap-4'>
						<InputGroup
							label='Citação oficial, que define o endereço'
							id='draftTitle'
							hint='Tendo número e ano, escreva-os: "Lei nº 13.105/2015" endereça lei-13105-2015.'
						>
							<Input
								type='text'
								value={draftTitle}
								onChange={(event) => setDraftTitle(event.target.value)}
							/>
						</InputGroup>

						<InputGroup
							label='A quem se aplica'
							id='draftScopes'
							hint='Separe por vírgula. Vazio quer dizer nacional.'
						>
							<Input
								type='text'
								value={draftScopes}
								onChange={(event) => setDraftScopes(event.target.value)}
							/>
						</InputGroup>

						<InputGroup
							label='Outros nomes por onde a prova cita'
							id='draftNicknames'
							hint='Os nomes e siglas, separados por vírgula. O que ficar escrito aqui é a lista inteira: apagar um nome tira o apelido do acervo, e é assim que se conserta a sigla que estava levando à norma errada. A citação oficial acima continua valendo como apelido de qualquer forma.'
						>
							<Input
								type='text'
								value={draftNicknames}
								placeholder='Código Civil, CC'
								onChange={(event) => setDraftNicknames(event.target.value)}
							/>
						</InputGroup>
					</div>

					{retitleFailure && (
						<div className='mt-4'>
							<p className='text-red'>{retitleFailure.message}</p>
							{retitleFailure.detail && (
								<p className='mt-1 text-sm text-red'>{retitleFailure.detail}</p>
							)}
						</div>
					)}

					<Flex className='gap-2 mt-6'>
						<Button
							onClick={onRetitle}
							disabled={isRetitling || draftTitle.trim().length === 0}
						>
							{isRetitling ? 'Corrigindo...' : 'Salvar correção'}
						</Button>
						<Button variant='outline' onClick={() => setEditing(false)}>
							<>Cancelar</>
						</Button>
					</Flex>
				</div>
			)}

			{confirmingRemoval && (
				<p className='mt-3 text-red max-w-[820px]'>
					Retirar apaga a norma e todos os seus dispositivos do acervo. As
					questões já convertidas continuam com o texto que publicaram, mas
					conversões novas deixam de encontrar esta lei.
				</p>
			)}

			<Divider className='mt-6 mb-6' />

			<div className='grid grid-cols-3 gap-6'>
				<Field label='Endereço no acervo'>{document.slug}</Field>
				<Field label='Tipo'>{document.kind}</Field>
				<Field label='Situação'>{document.status}</Field>

				<Field label='Alcance'>
					{document.scopes.length === 0
						? 'Nacional, concorre em toda prova'
						: document.scopes.join(', ')}
				</Field>

				<Field label='Dispositivos no acervo'>
					{document.provisionCount}
					{looksTruncated && (
						<span className='block text-sm text-red'>
							poucos para uma norma deste tipo, pode ter entrado incompleto
						</span>
					)}
				</Field>

				<Field label='Formato da captura'>{document.sourceFormat ?? '—'}</Field>

				<Field label='Outros nomes'>
					{document.aliases.length > 0 ? (
						document.aliases.join(', ')
					) : (
						<span className='text-gray font-normal'>
							nenhum cadastrado, então a busca direta só chega por citação exata
						</span>
					)}
				</Field>

				{isOfficialAddress(document.sourceUrl) && (
					<Field label='Endereço oficial'>
						<a
							href={document.sourceUrl ?? ''}
							target='_blank'
							rel='noreferrer'
							className='text-blue underline break-all'
						>
							{document.sourceUrl}
						</a>
					</Field>
				)}

				<Field label='Entrou em'>{formatDate(document.createdAt)}</Field>
			</div>

			<div className='mt-8'>
				<Button variant='outline' onClick={() => navigate(baseUrl)}>
					<>Voltar ao acervo</>
				</Button>
			</div>
		</Template>
	)
}

export default LegalDocumentDetail
